import { Dialog } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRef, useState, useCallback } from 'react';
import { IoClose } from 'react-icons/io5';
import { LuSettings2 } from 'react-icons/lu';
import { FiCheckSquare } from 'react-icons/fi';
import { BsHouseDoor } from 'react-icons/bs';
import { HiOutlineUser } from 'react-icons/hi';
import { useUpdateTaskAutomation, useGetTaskAutomationById } from '../../../services/automation.queries';
import type { IUpdateTaskAutomationRequest, ICreateTaskAutomationRequest, IInlineChecklistItem, IAutomationItem, ITaskAutomationDetail } from '../../../types/tasks.types';
import { useToast } from '../../../../../context/ToastContext';
import EditStepOne from './EditStepOne';
import EditStepTwo from './EditStepTwo';
import EditStepThree from './EditStepThree';
import EditStepFour from './EditStepFour';
import type { IEditStep1FormValues } from './EditStepOne';
import type { IEditStep2FormValues } from './EditStepTwo';
import type { IEditStep3FormValues } from './EditStepThree';
import type { IEditStep4FormValues } from './EditStepFour';

interface IEditTaskModalProps {
  open: boolean;
  task: IAutomationItem | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const STEP_HEADERS: Record<number, { title: string; description: string }> = {
  1: { title: 'Automation.editTask', description: 'Automation.editTaskDescription' },
  2: { title: 'Automation.editTaskList', description: 'Automation.editTaskListDescription' },
  3: { title: 'Automation.editSelectedProperties', description: 'Automation.editSelectedPropertiesDescription' },
  4: { title: 'Automation.editAssignment', description: 'Automation.editAssignmentDescription' },
};

interface IStepConfig {
  key: number;
  labelKey: string;
  icon: React.ReactNode;
}

const STEPS: IStepConfig[] = [
  { key: 1, labelKey: 'Automation.taskDetails', icon: <LuSettings2 size={18} /> },
  { key: 2, labelKey: 'Automation.taskListSidebar', icon: <FiCheckSquare size={18} /> },
  { key: 3, labelKey: 'Automation.selectPropertiesSidebar', icon: <BsHouseDoor size={18} /> },
  { key: 4, labelKey: 'Automation.assignTaskSidebar', icon: <HiOutlineUser size={18} /> },
];

const EditTaskModal = ({ open, task, onClose, onSuccess }: IEditTaskModalProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const isRtl = language === 'ar';
  const { showToast } = useToast();

  const [currentStep, setCurrentStep] = useState(1);
  const [step1Data, setStep1Data] = useState<IEditStep1FormValues | null>(null);
  const [step2Data, setStep2Data] = useState<IEditStep2FormValues | null>(null);
  const [step3Data, setStep3Data] = useState<IEditStep3FormValues | null>(null);

  const stepSubmitRef = useRef<(() => void) | null>(null);
  const updateMutation = useUpdateTaskAutomation();

  // Fetch full task automation detail by ID
  const {
    data: automationDetailData,
    isLoading: isDetailLoading,
  } = useGetTaskAutomationById(
    { id: task?.id ?? 0 },
    { enabled: open && !!task?.id }
  );
  const automationDetail: ITaskAutomationDetail | null = automationDetailData?.result ?? null;

  const handleSave = () => {
    stepSubmitRef.current?.();
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    } else {
      handleClose();
    }
  };

  const handleStep1Submit = useCallback((data: IEditStep1FormValues) => {
    setStep1Data(data);
    setCurrentStep(2);
  }, []);

  const handleStep2Submit = useCallback((data: IEditStep2FormValues) => {
    setStep2Data(data);
    setCurrentStep(3);
  }, []);

  const handleStep3Submit = useCallback((data: IEditStep3FormValues) => {
    setStep3Data(data);
    setCurrentStep(4);
  }, []);

  const handleStep4Submit = useCallback(
    (data: IEditStep4FormValues) => {
      if (!step1Data || !step2Data || !step3Data || !task) return;

      // Build inline checklist items — use label based on current language
      const checklistItems: IInlineChecklistItem[] = step2Data.selectedItemsData.map((item) => ({
        label: (isRtl ? item.labelAr : item.labelEn) || item.labelEn || item.labelAr || '',
        isActive: true,
      }));

      const body: ICreateTaskAutomationRequest = {
        name: step1Data.name,
        description: 'Automation Task Description',
        eventType: Number(step1Data.eventTypeId),
        eventTiming: Number(step1Data.eventTimingId),
        timeType: step1Data.timeType ? Number(step1Data.timeType) : null,
        time: step1Data.time ? Number(step1Data.time) : null,
        propertiesIds: step3Data.selectedPropertyIds,
        serviceProviderId: data.assigneeId ?? null,
        categoryId: step1Data.serviceId || '',
        categoryName: step1Data.serviceName || step1Data.serviceId || '',
        checklistItems,
      };

      const request: IUpdateTaskAutomationRequest = {
        id: task.id,
        body,
      };

      updateMutation.mutate(request, {
        onSuccess: (response) => {
          if (response.isSuccess) {
            handleClose();
            showToast(t('Automation.editTaskSuccess'), { severity: 'success' });
            onSuccess?.();
          } else {
            const msg = response.errorMessages?.join(', ') || t('Automation.editTaskError');
            showToast(msg, { severity: 'error' });
          }
        },
        onError: () => {
          showToast(t('Automation.editTaskError'), { severity: 'error' });
        },
      });
    },
    [step1Data, step2Data, step3Data, task, updateMutation, onSuccess]
  );

  const handleClose = () => {
    setCurrentStep(1);
    setStep1Data(null);
    setStep2Data(null);
    setStep3Data(null);
    onClose();
  };

  const handleStepClick = (stepKey: number) => {
    if (stepKey < currentStep) {
      // Go back to a completed step directly
      setCurrentStep(stepKey);
    } else if (stepKey > currentStep) {
      // Validate current step before advancing
      stepSubmitRef.current?.();
    }
  };

  if (!task) return null;

  const header = STEP_HEADERS[currentStep] || STEP_HEADERS[1];
  const isSaveDisabled = updateMutation.isPending;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      PaperProps={{
        className: '!w-[840px] !max-h-[90vh] !min-h-[600px] !rounded-[24px] !shadow-[0px_12px_138.6px_0px_rgba(0,0,0,0.25)] !overflow-hidden !flex !flex-col',
      }}
    >
      {/* ── Fixed Header: Close + Title ── */}
      <div className="shrink-0 pt-6 px-6 flex flex-col">
        <div className="flex justify-end">
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            type="button"
          >
            <IoClose size={24} color="#000" />
          </button>
        </div>

        {/* Title + Description - centered */}
        <div className="pt-2 pb-4" style={{ textAlign: 'start' }}>
          <h2
            style={{
              margin: 0,
              fontFamily: 'Tajawal, sans-serif',
              fontWeight: 700,
              fontSize: 20,
              color: '#261B6A',
              lineHeight: '34px',
            }}
          >
            {t(header.title)}
          </h2>
          <p
            style={{
              margin: 0,
              fontFamily: 'Tajawal, sans-serif',
              fontWeight: 400,
              fontSize: 14,
              color: '#8E98A1',
              lineHeight: '24px',
            }}
          >
            {t(header.description)}
          </p>
        </div>
      </div>

      {/* ── Main content: step content + sidebar ── */}
      <div className="flex-1 min-h-0 flex overflow-hidden">

        {/* Vertical Sidebar Stepper - always on the right visually */}
        <div
          className="shrink-0 flex flex-col gap-3 px-4 pt-2"
          style={{ width: 189}}
        >
          {STEPS.map((step) => {
            const isActive = step.key === currentStep;
            const isCompleted = step.key < currentStep;
            return (
              <button
                key={step.key}
                type="button"
                onClick={() => handleStepClick(step.key)}
                className="outline-none"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: isActive ? '1px solid #5B3FFF' : 'none',
                  backgroundColor: isActive ? '#E1DCFF' : '#FFFFFF',
                  boxShadow: '0px 0px 1px 0px rgba(12,26,75,0.1), 0px 4px 20px 0px rgba(50,50,71,0.08)',
                  cursor: isCompleted || isActive ? 'pointer' : 'default',
                  width: '100%',
                }}
              >
                <p
                  style={{
                    fontFamily: 'Tajawal, sans-serif',
                    fontWeight: 500,
                    fontSize: 12,
                    color: isActive ? '#5B3FFF' : '#8E98A1',
                    margin: 0,
                    flex: 1,
                    textAlign: 'start',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t(step.labelKey)}
                </p>
                <span style={{ color: isActive ? '#5B3FFF' : '#8E98A1', flexShrink: 0 }}>
                  {step.icon}
                </span>
              </button>
            );
          })}
        </div>
        {/* Step Content Area */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="px-6 pb-4 flex-1 min-h-[200px] overflow-y-auto">
            {isDetailLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8" style={{ border: '2px solid #E8E8EA', borderTopColor: '#5B3FFF' }} />
              </div>
            ) : (
              <>
                <div style={{ display: currentStep === 1 ? '' : 'none' }}>
                  <EditStepOne
                    task={task}
                    automationDetail={automationDetail}
                    isActive={currentStep === 1}
                    onSubmit={handleStep1Submit}
                    submitRef={stepSubmitRef}
                  />
                </div>
                <div style={{ display: currentStep === 2 ? '' : 'none' }}>
                  <EditStepTwo
                    serviceId={step1Data?.serviceId ?? automationDetail?.categoryId ?? task.serviceId ?? ''}
                    isActive={currentStep === 2}
                    initialSelectedLabels={automationDetail?.checklistItems?.flatMap((c) => [c.labelAr ?? '', c.labelEn ?? ''].filter(Boolean))}
                    onSubmit={handleStep2Submit}
                    submitRef={stepSubmitRef}
                  />
                </div>
                <div style={{ display: currentStep === 3 ? '' : 'none' }}>
                  <EditStepThree
                    initialSelectedPropertyIds={automationDetail?.properties?.map((p) => p.id)}
                    isActive={currentStep === 3}
                    onSubmit={handleStep3Submit}
                    submitRef={stepSubmitRef}
                  />
                </div>
                <div style={{ display: currentStep === 4 ? '' : 'none' }}>
                  <EditStepFour
                    initialAssigneeId={automationDetail?.serviceProviderId ?? task.serviceProviderId}
                    isActive={currentStep === 4}
                    onSubmit={handleStep4Submit}
                    submitRef={stepSubmitRef}
                  />
                </div>
              </>
            )}
          </div>
        </div>

      </div>

      {/* ── Fixed Footer ── */}
      <div
        className="shrink-0 w-full flex justify-between items-center gap-[10px] px-[30px] pt-[25px] pb-6"
        style={{ borderTop: '1px solid #E8E8EA', borderRadius: '0 0 24px 24px' }}
      >
                {/* Back button - visually on the right */}
        <button
          onClick={handleBack}
          type="button"
          className="cursor-pointer outline-none"
          style={{
            width: 200,
            height: 48,
            backgroundColor: '#FFFFFF',
            borderRadius: 8,
            border: '1px solid #5B3FFF',
            fontFamily: 'Tajawal, sans-serif',
            fontWeight: 700,
            fontSize: 14,
            color: '#5B3FFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {t('Automation.back')}
        </button>
        {/* Save/Next button - visually on the left */}
        <button
          onClick={handleSave}
          type="button"
          disabled={updateMutation.isPending}
          className="cursor-pointer outline-none"
          style={{
            width: 200,
            height: 48,
            backgroundColor: '#5B3FFF',
            borderRadius: 8,
            border: 'none',
            fontFamily: 'Tajawal, sans-serif',
            fontWeight: 700,
            fontSize: 14,
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: updateMutation.isPending ? 0.7 : 1,
          }}
        >
          {updateMutation.isPending
            ? '...'
            : currentStep === 4
              ? t('Automation.save')
              : t('Automation.next')}
        </button>

      </div>
    </Dialog>
  );
};

export default EditTaskModal;
